import api from './api';
import { Page } from './warehouse.service';

export type MatchStatus = 'matched' | 'price_variance' | 'unmatched';
export type ItcStatus = 'undecided' | 'eligible' | 'ineligible';
export type SupplyType = 'intra_state' | 'inter_state';

export interface VendorBillLine {
  id: string;
  goodsReceiptItemId: string | null;
  grnNumber: string | null;
  description: string;
  sku: string | null;
  unit: string | null;
  hsnCode: string | null;
  quantity: number;
  unitPrice: number;
  // The price of the purchase order line; null for a line that is not against a receipt
  orderedPrice: number | null;
  taxRate: number;
  taxableAmount: number;
  taxAmount: number;
}

export interface VendorBill {
  id: string;
  billNumber: string;
  vendorInvoiceNumber: string;
  billDate: string;
  dueDate: string;
  vendorId: string;
  vendorName: string | null;
  purchaseOrderNumber: string | null;
  status: 'posted' | 'paid' | 'cancelled';
  matchStatus: MatchStatus;
  itcStatus: ItcStatus;
  supplyType?: SupplyType;
  vendorGSTIN?: string | null;
  taxableAmount: number;
  cgstAmount?: number;
  sgstAmount?: number;
  igstAmount?: number;
  taxAmount: number;
  otherCharges?: number;
  grandTotal: number;
  paidAmount: number;
  balance: number;
  daysOverdue?: number;
  notes?: string | null;
  cancelReason?: string | null;
  items?: VendorBillLine[];
  payments?: { paymentId: string; paymentNumber: string; paymentDate: string; mode: string; amount: number }[];
}

export interface BillableLine {
  goodsReceiptItemId: string;
  grnNumber: string;
  receiptDate: string;
  purchaseOrderNumber: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  hsnCode: string | null;
  acceptedQuantity: number;
  billedQuantity: number;
  billableQuantity: number;
  orderedPrice: number;
  taxRate: number;
}

export interface VendorBillSummary {
  bills: number;
  openBills: number;
  payable: number;
  overdueBills: number;
  overdue: number;
  toReview: number;
  itcUndecided: number;
  unbilledReceiptLines: number;
  unbilledReceiptValue: number;
}

export interface VendorBillInput {
  vendorId: string;
  vendorInvoiceNumber: string;
  billDate: string;
  dueDate?: string;
  supplyType?: SupplyType;
  otherCharges?: number;
  itcStatus?: ItcStatus;
  notes?: string | null;
  items: { goodsReceiptItemId?: string; description?: string; hsnCode?: string | null; quantity: number; unitPrice: number; taxRate: number }[];
}

export interface VendorPayment {
  id: string;
  paymentNumber: string;
  paymentDate: string;
  vendorId: string;
  vendorName: string | null;
  amount: number;
  allocatedAmount: number;
  // Paid and not yet set against a bill
  unallocatedAmount: number;
  mode: string;
  referenceNumber: string | null;
  bankName?: string | null;
  notes?: string | null;
  status: 'posted' | 'cancelled';
  cancelReason?: string | null;
  allocations?: { id: string; vendorBillId: string; billNumber: string; vendorInvoiceNumber: string; billDate: string; grandTotal: number; amount: number }[];
}

export interface VendorPaymentInput {
  vendorId: string;
  paymentDate?: string;
  amount: number;
  mode?: string;
  referenceNumber?: string | null;
  bankName?: string | null;
  notes?: string | null;
  allocations?: { vendorBillId: string; amount: number }[];
}

export interface PayablesAgeingRow {
  vendorId: string;
  vendorName: string | null;
  bills: number;
  notDue: number;
  days1to30: number;
  days31to60: number;
  days61to90: number;
  over90: number;
  total: number;
  advances: number;
}

export interface VendorBillQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  vendorId?: string;
  status?: string;
  matchStatus?: MatchStatus | '';
  unpaid?: boolean;
  overdueOnly?: boolean;
}

// Empty filters are left out of the request
const clean = (params: object) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== '' && value !== false));

export const payablesService = {
  getBills: async (params: VendorBillQueryParams): Promise<Page<VendorBill> & { totalBalance: number }> => (await api.get('/vendor-bills', { params: clean(params) })).data,
  getBillSummary: async (): Promise<VendorBillSummary> => (await api.get('/vendor-bills/summary')).data,
  getBillable: async (vendorId: string): Promise<BillableLine[]> => (await api.get(`/vendor-bills/billable/${vendorId}`)).data,
  getBill: async (id: string): Promise<VendorBill> => (await api.get(`/vendor-bills/${id}`)).data,
  createBill: async (data: VendorBillInput): Promise<VendorBill> => (await api.post('/vendor-bills', data)).data,
  setItc: async (id: string, itcStatus: ItcStatus): Promise<VendorBill> => (await api.patch(`/vendor-bills/${id}/itc`, { itcStatus })).data,
  cancelBill: async (id: string, reason: string): Promise<VendorBill> => (await api.post(`/vendor-bills/${id}/cancel`, { reason })).data,

  getPayments: async (params: { page?: number; limit?: number; search?: string; status?: string }): Promise<Page<VendorPayment>> =>
    (await api.get('/vendor-payments', { params: clean(params) })).data,
  getPaymentSummary: async (): Promise<{ payments: number; paidThisMonth: number; advances: number }> => (await api.get('/vendor-payments/summary')).data,
  getAgeing: async (): Promise<{ data: PayablesAgeingRow[]; totals: Record<string, number> }> => (await api.get('/vendor-payments/ageing')).data,
  getPayment: async (id: string): Promise<VendorPayment> => (await api.get(`/vendor-payments/${id}`)).data,
  createPayment: async (data: VendorPaymentInput): Promise<VendorPayment> => (await api.post('/vendor-payments', data)).data,
  allocatePayment: async (id: string, allocations: { vendorBillId: string; amount: number }[]): Promise<VendorPayment> =>
    (await api.post(`/vendor-payments/${id}/allocate`, { allocations })).data,
  cancelPayment: async (id: string, reason: string): Promise<VendorPayment> => (await api.post(`/vendor-payments/${id}/cancel`, { reason })).data,
};
